import { useState } from "react";
import { Button, V2Loader } from "web-design-system";
import { Client, Scheme } from "creso-core/types";
import {
  useCreateGiftOutboundOrder,
  useInvestorPrimaryBankAccount,
  useLrsFxQuote,
  useNotifyGiftPaymentLink,
} from "creso-core/hooks";
import { SuccessLottieAnimation } from "web-core/components";
import { handleError } from "@/utils/error";
import { Loader2Icon } from "lucide-react";
import { GiftFlowHeader } from "./gift-flow-header";

const NOTIFIED_DISPLAY_DURATION = 3000;

const formatUsd = (value: number) => `$${value.toLocaleString()}`;
const formatInr = (value: number) =>
  `₹${value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export interface GiftPaymentSummaryProps {
  client: Client;
  scheme: Scheme<"all">;
  amount: number;
  folioNumber: string;
  onBack: () => void;
  onNotified: () => void;
}

export function GiftPaymentSummary({
  client,
  scheme,
  amount,
  folioNumber,
  onBack,
  onNotified,
}: GiftPaymentSummaryProps) {
  const [notified, setNotified] = useState(false);
  const { data: bankAccount, isLoading: isLoadingBankAccount } =
    useInvestorPrimaryBankAccount(client.investment_account || "");

  const { data: quote, isLoading: isLoadingQuote } = useLrsFxQuote(
    {
      bankAccountId: bankAccount?.id ?? 0,
      amount,
      fundSchemeId: scheme.fund_scheme_id as number,
    },
    { enabled: !!bankAccount?.id }
  );

  const transactionFee = Number(quote?.service_fee?.amount ?? 0);
  const subtotal = amount + transactionFee;
  const fxRate = quote ? Number(quote.fx_rate) : 0;
  const gst = quote ? Number(quote.total_tax) : 0;
  const inrAmount = quote ? Number(quote.from.amount) - gst : subtotal * fxRate;
  const payable = quote ? Number(quote.from.amount) : inrAmount + gst;

  const maskedAccountNumber = bankAccount?.account_number
    ? `*${bankAccount.account_number.slice(-4)}`
    : "";

  const createOrder = useCreateGiftOutboundOrder();

  const notifyOrder = useNotifyGiftPaymentLink();

  const notify = async () => {
    if (!bankAccount || !scheme) return;

    try {
      const order = await createOrder.mutateAsync({
        bankAccountId: bankAccount.id,
        fundSchemeId: scheme.fund_scheme_id as number,
        amount,
        folioNo: folioNumber,
        investmentAccountId: client.investment_account ?? undefined,
      });
      await notifyOrder.mutateAsync({ id: order.id });
      setNotified(true);
      setTimeout(onNotified, NOTIFIED_DISPLAY_DURATION);
    } catch (error) {
      handleError(error, "Failed to notify for payment");
    }
  };

  if (notified) {
    return (
      <div className="flex flex-col h-full min-h-0 items-center justify-center gap-4">
        <SuccessLottieAnimation loop={false} height={150} width={150} />
        <span className="text-sm font-semibold text-foreground">
          Payment notification sent
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <GiftFlowHeader
        title="Payment Summary"
        step={3}
        totalSteps={3}
        onBack={onBack}
      />
      <V2Loader
        isLoading={isLoadingBankAccount || isLoadingQuote}
        className="flex flex-1"
      >
        <div className="flex-1 min-h-0 text-sm overflow-y-auto pt-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-foreground">Investing amount</span>
            <span className="font-semibold text-foreground">
              {formatUsd(amount)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-foreground">Transaction fee</span>
            <span className="font-semibold text-foreground">
              {formatUsd(transactionFee)}
            </span>
          </div>

          <div className="border-t border-border" />

          <div className="flex items-center justify-between">
            <span className="font-semibold text-foreground">Subtotal</span>
            <span className="font-semibold text-foreground">
              {formatUsd(subtotal)}
            </span>
          </div>

          <div className="border-t border-border" />

          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Fx rate ~{fxRate}</span>
            <span className="text-muted-foreground">
              ~{formatInr(inrAmount)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">GST</span>
            <span className="text-muted-foreground">{formatInr(gst)}</span>
          </div>

          <div className="border-t border-border mt-auto" />

          <div className="flex items-center justify-between">
            <div>
              <span className="font-semibold text-foreground">
                Payable amount
              </span>
              <p className="text-xs text-muted-foreground">
                From: {bankAccount?.bank_name} {maskedAccountNumber}
              </p>
            </div>
            <span className="font-semibold text-foreground">
              {formatInr(payable)}
            </span>
          </div>
        </div>

        <div className="pt-6 flex items-center gap-2">
          <Button
            disabled={createOrder.isPending || notifyOrder.isPending}
            onClick={notify}
            className="flex-1"
          >
            {(createOrder.isPending || notifyOrder.isPending) && (
              <Loader2Icon className="animate-spin" />
            )}
            Notify for payment
          </Button>
        </div>
      </V2Loader>
    </div>
  );
}
